import React, { useState, useCallback, useRef, useMemo, useEffect } from 'react';
import { Stage, Layer, Rect } from 'react-konva';
import AnnotationBlock from './AnnotationBlock';
import {
  calculatePosition as calculatePositionUtil,
  calculateTotalWidth as calculateTotalWidthUtil,
  toMusicalPositionWithGridLevel as toMusicalPositionUtil,
  calculateVisibleBeats,
  getGridLevel,
  findLeftRightAnnotationBoundaries,
  computeMinWidth,
} from '../utils/annotationUtils';
import {
  clampResizeInterval,
  comparePositions,
  intervalsOverlap,
} from '../utils/annotationCollision';
import { DEFAULT_PIXELS_PER_BEAT, GRID_LEVELS } from '../utils/constants';
import {Popover} from "@mui/material";
import AnnotationForm from "./AnnotationForm";
import { FixedSizeList as List } from 'react-window';
import { useViewMode } from '../contexts/ViewModeContext';
import { useAnnotation } from '../contexts/AnnotationContext';
import { createAnnotationDraftController } from '../state/annotationDraftController';
import {
  planBlockMove,
  planBlockResize,
  planGroupMove,
  selectMarqueeAnnotations,
} from '../utils/annotationGroupOperations';
import useTimelineEdgeAutoScroll from '../hooks/useTimelineEdgeAutoScroll';
import { createAnnotationBlockDefaults } from '../utils/annotationBlockDefaults';
import {
  canOpenAnnotationForm,
  canSelectAnnotationBlock,
} from '../utils/annotationBlockInteraction';
import {
  getSelectionPopoverPosition,
  observeSelectionGeometry,
} from '../utils/annotationSelectionGeometry';

// Constants
const MIN_ANNOTATION_WIDTH = 0.5; // In beats
const PREVIEW_FILL_COLOR = "rgba(0, 0, 255, 0.2)";
const PREVIEW_STROKE_COLOR = "rgba(0, 0, 255, 0.5)";
const PREVIEW_STROKE_WIDTH = 1;

const toInterval = annotation => ({
  start: [
    annotation.startMeasure,
    annotation.startBeat,
    annotation.startSubdivision || 0,
  ],
  end: [
    annotation.endMeasure,
    annotation.endBeat,
    annotation.endSubdivision || 0,
  ],
});

const toPositionPatch = interval => ({
  startMeasure: interval.start[0],
  startBeat: interval.start[1],
  startSubdivision: interval.start[2],
  endMeasure: interval.end[0],
  endBeat: interval.end[1],
  endSubdivision: interval.end[2],
});

const intervalsEqual = (left, right) => (
  comparePositions(left.start, right.start) === 0 &&
  comparePositions(left.end, right.end) === 0
);

/**
 * Optimized BeatLines component - renders beat lines dynamically based on grid level
 */
export const BeatLines = ({
  measures, 
  beatsPerMeasureMap, 
  calculatePosition, 
  actualHeight, 
  visibleRange, 
  scrollPosition, 
  viewportWidth,
  currentGridLevel,
  timeSignatures,
  actualPixelsPerBeat
}) => {
  // Calculate visible beat positions with dynamic grid level support
  const beatPositions = useMemo(() => {
    const positions = [];
    const startMeasure = Math.max(0, visibleRange.start);
    const endMeasure = Math.min(measures, visibleRange.end);
    
    // Early exit if no measures to render
    if (startMeasure >= endMeasure) return positions;
    
    // Use more precise viewport boundaries with smaller buffer
    const viewportStart = scrollPosition - 50;
    const viewportEnd = scrollPosition + viewportWidth + 50;
    
    // Determine what to show based on grid level
    const showBeats = currentGridLevel === GRID_LEVELS.BEAT || currentGridLevel === GRID_LEVELS.SUBDIVISION;
    const showSubdivisions = currentGridLevel === GRID_LEVELS.SUBDIVISION;
    
    for (let measure = startMeasure; measure <= endMeasure; measure++) {
      if (measure >= measures) break;
      
      const beatsInMeasure = beatsPerMeasureMap[measure] || 4;
      
      for (let beat = 1; beat <= beatsInMeasure; beat++) {
        const x = calculatePosition(measure + 1, beat, 1);
        const displayX = x - scrollPosition;
        
        // More efficient viewport check
        if (x >= viewportStart && x <= viewportEnd) {
          const isMeasureLine = beat === 1;
          
          // Always show measure lines (bar level)
          if (isMeasureLine) {
            positions.push({
              x: displayX,
              width: 2,
              height: actualHeight,
              fill: "#333",
              key: `measure-${measure}-${beat}`,
              type: 'measure'
            });
          }
          // Show beat lines only if current grid level allows it
          else if (showBeats) {
            positions.push({
              x: displayX,
              width: 1,
              height: actualHeight,
              fill: "#ddd",
              key: `beat-${measure}-${beat}`,
              type: 'beat'
            });
          }
          
          // Add subdivision lines if enabled
          if (showSubdivisions) {
            // Get subdivisions per beat for this measure
            const subdivisionsPerBeat = timeSignatures && timeSignatures[measure] 
              ? (() => {
                  const parts = timeSignatures[measure].split('/');
                  const denominator = parseInt(parts[1], 10) || 4;
                  return denominator >= 16 ? 0 : Math.floor(16 / denominator);
                })()
              : 4; // Default to 4 subdivisions
            
            if (subdivisionsPerBeat > 0) {
              for (let sub = 2; sub <= subdivisionsPerBeat; sub++) {
                const subX = x + ((sub - 1) / subdivisionsPerBeat) * actualPixelsPerBeat;
                const subDisplayX = subX - scrollPosition;
                
                if (subX >= viewportStart && subX <= viewportEnd) {
                  // Calculate subdivision line dimensions - short and centered
                  const subdivisionHeight = Math.max(5, actualHeight * 0.1); // 30% of track height, minimum 20px
                  const subdivisionY = (actualHeight - subdivisionHeight) / 2; // Center vertically
                  
                  positions.push({
                    x: subDisplayX,
                    y: subdivisionY,
                    width: 0.5, 
                    height: subdivisionHeight,
                    fill: "#e0e0e0", 
                    key: `subdivision-${measure}-${beat}-${sub}`,
                    type: 'subdivision'
                  });
                }
              }
            }
          }
        }
      }
    }
    
    // Add the N+1 bar line at the end of all measures
    const nPlusOneBarX = calculatePosition(measures + 1, 1, 1);
    const nPlusOneBarDisplayX = nPlusOneBarX - scrollPosition;
    
    // Check if the N+1 bar line is visible in the viewport
    if (nPlusOneBarX >= viewportStart && nPlusOneBarX <= viewportEnd) {
      positions.push({
        x: nPlusOneBarDisplayX,
        width: 2,
        height: actualHeight,
        fill: "#333",
        key: `end-bar`,
        type: 'measure'
      });
    }
    
    return positions;
  }, [measures, beatsPerMeasureMap, calculatePosition, actualHeight, visibleRange, scrollPosition, viewportWidth, currentGridLevel, timeSignatures, actualPixelsPerBeat]);

  // Render as individual Rect elements for better performance than paths
  return (
    <>
      {beatPositions.map(({ x, y = 0, width, height, fill, key }) => (
        <Rect
          key={key}
          x={x}
          y={y}
          width={width}
          height={height}
          fill={fill}
        />
      ))}
    </>
  );
};

// Item renderer for virtualized annotation blocks
const VirtualizedAnnotationBlock = ({ index, style, data }) => {
  const {
    annotations,
    selectedId,
    actualPixelsPerBeat,
    beatsPerMeasureMap,
    calculatePosition,
    actualHeight,
    collapsed,
    onSelect,
    onAnnotationDrag,
    onAnnotationResize,
    handleSelectAnnotation,
    onAnnotationDelete
  } = data;

  const annotation = annotations[index];
  const otherAnnotations = annotations.filter(a => a.id !== annotation.id && a.trackId === annotation.trackId);

  return (
    <div style={style}>
      <AnnotationBlock
        key={annotation.id}
        annotation={annotation}
        pixelsPerBeat={actualPixelsPerBeat}
        beatsPerMeasureMap={beatsPerMeasureMap}
        calculatePosition={calculatePosition}
        height={actualHeight - 10}
        isSelected={selectedId === annotation.id}
        collapsed={collapsed}
        otherAnnotations={otherAnnotations}
        onSelect={() => {
          onSelect(annotation.id);
        }}
        onDragMove={(newPosition) => onAnnotationDrag(annotation.id, newPosition)}
        onResize={(newWidth) => onAnnotationResize(annotation.id, newWidth)}
        onDoubleClick={(e) => handleSelectAnnotation(e, annotation)}
        onDelete={() => {
          // console.log('Deleting annotation:', annotation.id);
          onAnnotationDelete(annotation.id);
        }}
      />
    </div>
  );
};

/**
 * AnnotationLayer component - optimized implementation with viewport-based rendering
 */
const AnnotationLayer = ({
  trackId,
  annotations = [],
  timeSignatures = [],
  beatsPerMeasureMap = [],
  measures = 16,
  zoomLevel = 1,
  pixelsPerBeat = 40,
  height = 100,
  collapsed = false,
  onAnnotationCreate = () => {},
  onAnnotationUpdate = () => {},
  onAnnotationSelect = (id) => {},
  onAnnotationDelete = () => {},
  annotationFormAttributes = null,
  // Updated props for better scrolling
  sharedScrollContainer = null,
  className = "",
  sharedScrollLeft = 0,
  trackColor,
  sharedGridLevel = 'bar',
  isSelected = false,
  isMarkedForDeletion = false,
  toolMode = 'pencil',
  selectedIds = [],
  copiedSourceIds = [],
  pastedIds = [],
  isClipboardTrack = false,
  onSelectionChange = () => {},
  onBlockSelectionRequest = () => {},
  onClipboardContextMenu = () => {},
  onSelectionGeometry = () => {},
  onAnnotationsMove = () => ({ success: false }),
}) => {
  // State
  const [isDrawing, setIsDrawing] = useState(false);
  const [drawStartX, setDrawStartX] = useState(null);
  const [drawCurrentX, setDrawCurrentX] = useState(null);
  const stageRef = useRef(null);
  const containerRef = useRef(null);
  const [visibleRange, setVisibleRange] = useState({ start: 0, end: 10 });
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [viewportWidth, setViewportWidth] = useState(window.innerWidth);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [marquee, setMarquee] = useState(null);
  const [groupPreviewDelta, setGroupPreviewDelta] = useState(0);
  const marqueeRef = useRef(null);
  const marqueeLastClientXRef = useRef(null);
  const groupDragRef = useRef(null);
  const {isViewMode, isTrackEditMode} = useViewMode();
  const interactionDisabled = isViewMode || isTrackEditMode;
  const canOpenForm = canOpenAnnotationForm({ isViewMode, isTrackEditMode });
  const canSelectBlock = canSelectAnnotationBlock({ isViewMode, isTrackEditMode });
  const [draft, setDraft] = useState(null);
  const annotationUpdateRef = useRef(onAnnotationUpdate);
  annotationUpdateRef.current = onAnnotationUpdate;
  const draftControllerRef = useRef(null);
  if (draftControllerRef.current === null) {
    draftControllerRef.current = createAnnotationDraftController({
      commit: (blockId, values) => {
        annotationUpdateRef.current(blockId, { formData: values });
      },
    });
  }
  const draftController = draftControllerRef.current;

  // Use global selection from context
  const {
    selectedAnnotationId,
    activeFormBlockId,
    handleAnnotationSelect,
    handleAnnotationUpdate,
    openAnnotationForm,
    closeAnnotationForm,
  } = useAnnotation();
  const draftOwner = useMemo(
    () => draft ? annotations.find(annotation => annotation.id === draft.blockId) : null,
    [annotations, draft]
  );

  // annotation form open / close
  const handleSelectAnnotation = (event, annotation) => {
    if (isMarkedForDeletion) return; // Prevent form opening for deleted tracks

    const currentDraft = draftController.getDraft();
    if (currentDraft && currentDraft.blockId !== annotation.id) {
      draftController.close();
    }
    if (!currentDraft || currentDraft.blockId !== annotation.id) {
      draftController.open(annotation.id, annotation.formData || {});
    }
    setDraft(draftController.getDraft());
    setAnchorEl(event.currentTarget);
    openAnnotationForm(annotation.id);
    onAnnotationSelect(annotation.id);
  };

  const handleCloseAnnotation = () => {
    const ownerId = draftController.getDraft()?.blockId || draft?.blockId;
    draftController.close();
    setDraft(null);
    setAnchorEl(null);
    if (ownerId) closeAnnotationForm(ownerId);
  };
  
  // Calculations
  const actualPixelsPerBeat = pixelsPerBeat * zoomLevel;
  const actualHeight = collapsed ? 30 : height;
  
  // Use shared grid level from TimeGrid instead of calculating independently
  const currentGridLevel = sharedGridLevel;
  
  // Safety check for excessive height that could cause rendering issues
  const MAX_SAFE_CANVAS_HEIGHT = 400; // Maximum recommended canvas height for stable rendering
  const isTooTall = actualHeight > MAX_SAFE_CANVAS_HEIGHT;
  
  // Calculate viewport-based width instead of total width for better performance
  const viewportCanvasWidth = useMemo(() => {
    const containerWidth = containerRef.current?.clientWidth || viewportWidth;
    // Use viewport width instead of total width for the Canvas
    return Math.min(containerWidth * 1.2, 4000); // Cap at 4000px for performance
  }, [viewportWidth]);
  
  const totalWidth = useMemo(() => 
    calculateTotalWidthUtil(measures, beatsPerMeasureMap, actualPixelsPerBeat),
    [measures, beatsPerMeasureMap, actualPixelsPerBeat]
  );
  
  // Utility functions
  const calculatePosition = useCallback((measure, beat, subdivision = 1) => {
    return calculatePositionUtil(measure, beat, subdivision, beatsPerMeasureMap, actualPixelsPerBeat, timeSignatures);
  }, [beatsPerMeasureMap, actualPixelsPerBeat, timeSignatures]);
  
  const toMusicalPosition = useCallback((x, doProximity) => {
    return toMusicalPositionUtil(x, currentGridLevel, doProximity, beatsPerMeasureMap, actualPixelsPerBeat, timeSignatures);
  }, [beatsPerMeasureMap, actualPixelsPerBeat, currentGridLevel, timeSignatures]);

  const fromValue = useCallback(value => {
    const position = toMusicalPosition(value, false);
    return [position.measure, position.beat, position.subdivision || 0];
  }, [toMusicalPosition]);

  // Filter annotations by trackId
  const trackAnnotations = useMemo(() => {
    return annotations.filter(a => a.trackId === trackId);
  }, [annotations, trackId]);
  const selectedIdSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const copiedIdSet = useMemo(() => new Set(copiedSourceIds), [copiedSourceIds]);
  const pastedIdSet = useMemo(() => new Set(pastedIds), [pastedIds]);

  const absolutePointerX = useCallback(clientX => {
    const bounds = containerRef.current?.getBoundingClientRect();
    if (!bounds) return 0;
    const currentScroll = sharedScrollContainer?.scrollLeft ?? scrollPosition;
    return Math.max(0, clientX - bounds.left + currentScroll);
  }, [scrollPosition, sharedScrollContainer]);

  const updateMarqueeSelection = useCallback((startX, currentX) => {
    const ids = selectMarqueeAnnotations({
      annotations: trackAnnotations,
      trackId,
      startValue: startX,
      endValue: currentX,
      toValue: position => calculatePosition(...position),
    });
    onSelectionChange({
      trackId,
      ids,
      primaryId: ids[0] || null,
      anchorId: ids[ids.length - 1] || null,
    });
  }, [calculatePosition, onSelectionChange, trackAnnotations, trackId]);

  const calculateGroupPlan = useCallback(clientX => {
    const drag = groupDragRef.current;
    if (!drag) return null;
    const currentScroll = sharedScrollContainer?.scrollLeft ?? scrollPosition;
    const proposedDelta = clientX - drag.originClientX + currentScroll - drag.originScrollLeft;
    return planGroupMove({
      annotations: trackAnnotations,
      selectedIds: drag.selectedIds,
      proposedDelta,
      trackId,
      sheetStart: calculatePosition(1, 1, 1),
      sheetEnd: calculatePosition(measures + 1, 1, 1),
      toValue: position => calculatePosition(...position),
      fromValue,
      movementStep: actualPixelsPerBeat / 4,
    });
  }, [actualPixelsPerBeat, calculatePosition, fromValue, measures, scrollPosition, sharedScrollContainer, trackAnnotations, trackId]);

  const handleAutoScroll = useCallback(() => {
    const clientX = marqueeLastClientXRef.current;
    if (clientX === null) return;
    const activeMarquee = marqueeRef.current;
    if (activeMarquee) {
      const currentX = absolutePointerX(clientX);
      const next = { ...activeMarquee, currentX };
      marqueeRef.current = next;
      setMarquee(next);
      updateMarqueeSelection(next.startX, currentX);
    }
    if (groupDragRef.current) {
      const plan = calculateGroupPlan(clientX);
      if (plan?.success) setGroupPreviewDelta(plan.delta);
    }
  }, [absolutePointerX, calculateGroupPlan, updateMarqueeSelection]);

  const {
    updatePointer: updateEdgePointer,
    stop: stopEdgeScroll,
  } = useTimelineEdgeAutoScroll({
    container: sharedScrollContainer,
    onScroll: handleAutoScroll,
  });

  const beginMarquee = useCallback((clientX, { clearOnClick = false } = {}) => {
    if (toolMode !== 'cursor' || interactionDisabled || isMarkedForDeletion) return;
    const startX = absolutePointerX(clientX);
    const next = { startX, currentX: startX, originClientX: clientX, clearOnClick, moved: false };
    marqueeLastClientXRef.current = clientX;
    marqueeRef.current = next;
    setMarquee(next);
  }, [absolutePointerX, interactionDisabled, isMarkedForDeletion, toolMode]);

  const handleGroupDragStart = useCallback(({ clientX }) => {
    if (interactionDisabled || toolMode !== 'cursor' || selectedIds.length === 0) return;
    groupDragRef.current = {
      originClientX: clientX,
      originScrollLeft: sharedScrollContainer?.scrollLeft ?? scrollPosition,
      selectedIds: [...selectedIds],
      originalBlocks: trackAnnotations.filter(annotation => selectedIdSet.has(annotation.id)),
    };
    marqueeLastClientXRef.current = clientX;
    setGroupPreviewDelta(0);
  }, [interactionDisabled, scrollPosition, selectedIdSet, selectedIds, sharedScrollContainer, toolMode, trackAnnotations]);

  const handleGroupDragMove = useCallback(({ clientX }) => {
    if (!groupDragRef.current) return;
    marqueeLastClientXRef.current = clientX;
    updateEdgePointer(clientX);
    const plan = calculateGroupPlan(clientX);
    if (plan?.success) setGroupPreviewDelta(plan.delta);
  }, [calculateGroupPlan, updateEdgePointer]);

  const handleGroupDragEnd = useCallback(({ clientX }) => {
    const drag = groupDragRef.current;
    if (!drag) return;
    marqueeLastClientXRef.current = clientX;
    const plan = calculateGroupPlan(clientX);
    stopEdgeScroll();
    groupDragRef.current = null;
    marqueeLastClientXRef.current = null;
    setGroupPreviewDelta(0);
    if (!plan?.success) return;

    const changes = plan.intervals.map(item => ({ id: item.id, ...toPositionPatch(item.interval) }));
    const originalById = new Map(drag.originalBlocks.map(block => [block.id, toInterval(block)]));
    onAnnotationsMove(changes, {
      validate: ({ currentBlocks, proposedBlocks, trackBlocks }) => {
        const stale = currentBlocks.some(block => {
          const original = originalById.get(block.id);
          return !original || !intervalsEqual(original, toInterval(block));
        });
        if (stale) return { success: false, reason: 'stale-selection' };

        const movingIds = new Set(proposedBlocks.map(block => block.id));
        const stationaryIntervals = trackBlocks
          .filter(block => !movingIds.has(block.id))
          .map(toInterval);
        const collides = proposedBlocks.some(block => (
          stationaryIntervals.some(interval => intervalsOverlap(toInterval(block), interval))
        ));
        return collides ? { success: false, reason: 'collision' } : true;
      },
    });
  }, [calculateGroupPlan, onAnnotationsMove, stopEdgeScroll]);

  // Optimized visible range calculation with better buffering
  const updateVisibleRange = useCallback((scrollLeft) => {
    if (!containerRef.current) return;
    
    // Store scroll position for component updates
    setScrollPosition(scrollLeft);
    
    // Get viewport dimensions
    const clientWidth = containerRef.current?.clientWidth || viewportWidth;
    
    // Convert pixel positions to measure indexes with improved precision
    const startPos = toMusicalPosition(scrollLeft, false);
    const endPos = toMusicalPosition(scrollLeft + clientWidth, false);
    
    // Reduced buffer for better performance - only 1 measure on each side
    const startMeasure = Math.max(0, startPos.measure - 1);
    const endMeasure = Math.min(measures, endPos.measure + 1);
    

    setVisibleRange({
      start: startMeasure,
      end: endMeasure
    });
  }, [toMusicalPosition, measures, viewportWidth]);

  // A different active owner commits this layer's draft. Clearing ownership
  // entirely means the owner was deleted or the sheet was replaced, so cancel.
  useEffect(() => {
    const ownedDraft = draftController.getDraft();
    if (!ownedDraft) {
      if (draft && activeFormBlockId !== draft.blockId) {
        setDraft(null);
        setAnchorEl(null);
      }
      return;
    }
    if (activeFormBlockId === ownedDraft.blockId) return;

    if (activeFormBlockId === null) {
      draftController.discard();
    } else {
      draftController.close();
    }
    setDraft(null);
    setAnchorEl(null);
  }, [activeFormBlockId, draft, draftController]);

  useEffect(() => () => {
    draftController.dispose();
  }, [draftController]);

  useEffect(() => {
    if (marquee?.startX === undefined || marquee?.startX === null) return undefined;

    const handleWindowMouseMove = event => {
      const active = marqueeRef.current;
      if (!active) return;
      marqueeLastClientXRef.current = event.clientX;
      updateEdgePointer(event.clientX);
      const currentX = absolutePointerX(event.clientX);
      const next = {
        ...active,
        currentX,
        moved: active.moved || Math.abs(event.clientX - active.originClientX) >= 3,
      };
      marqueeRef.current = next;
      setMarquee(next);
      if (next.moved) updateMarqueeSelection(next.startX, currentX);
    };
    const handleWindowMouseUp = () => {
      const finished = marqueeRef.current;
      if (!finished?.moved && finished?.clearOnClick) {
        onSelectionChange({ trackId, ids: [], primaryId: null, anchorId: null });
      }
      stopEdgeScroll();
      marqueeRef.current = null;
      marqueeLastClientXRef.current = null;
      setMarquee(null);
    };

    window.addEventListener('mousemove', handleWindowMouseMove);
    window.addEventListener('mouseup', handleWindowMouseUp, { once: true });
    return () => {
      window.removeEventListener('mousemove', handleWindowMouseMove);
      window.removeEventListener('mouseup', handleWindowMouseUp);
    };
  }, [absolutePointerX, marquee?.startX, onSelectionChange, stopEdgeScroll, trackId, updateEdgePointer, updateMarqueeSelection]);

  useEffect(() => {
    if (toolMode === 'cursor') return;
    stopEdgeScroll();
    marqueeRef.current = null;
    groupDragRef.current = null;
    setMarquee(null);
    setGroupPreviewDelta(0);
  }, [stopEdgeScroll, toolMode]);

  const updateSelectionGeometry = useCallback(() => {
    if (toolMode !== 'cursor' || selectedIds.length === 0 || !containerRef.current) return;
    const selected = trackAnnotations.filter(annotation => selectedIdSet.has(annotation.id));
    if (selected.length === 0) return;
    const rightmost = Math.max(...selected.map(annotation => calculatePosition(
      annotation.endMeasure,
      annotation.endBeat,
      annotation.endSubdivision || 0
    )));
    const bounds = containerRef.current.getBoundingClientRect();
    const trackViewport = containerRef.current.closest('[data-testid="annotation-track-lanes"]');
    const viewportBounds = trackViewport?.getBoundingClientRect() || {
      top: 0,
      bottom: window.innerHeight,
    };
    onSelectionGeometry({
      trackId,
      position: getSelectionPopoverPosition({
        layerBounds: bounds,
        viewportBounds,
        rightmostX: rightmost,
        scrollLeft: scrollPosition,
        layerHeight: actualHeight,
      }),
    });
  }, [actualHeight, calculatePosition, onSelectionGeometry, scrollPosition, selectedIdSet, selectedIds.length, toolMode, trackAnnotations, trackId]);

  useEffect(() => {
    if (toolMode !== 'cursor' || selectedIds.length === 0 || !containerRef.current) return undefined;
    updateSelectionGeometry();
    return observeSelectionGeometry({
      element: containerRef.current,
      onChange: updateSelectionGeometry,
    });
  }, [selectedIds.length, toolMode, updateSelectionGeometry]);

  // Set up scroll observation with throttling for better performance
  useEffect(() => {
    if (!sharedScrollContainer) return;
    
    let animationFrameId;
    let lastUpdateTime = 0;
    const UPDATE_THROTTLE = 8; // ~120fps update limit for smoother performance
    
    const handleScroll = () => {
      const now = performance.now();
      
      // Use requestAnimationFrame to throttle scroll updates
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
      
      animationFrameId = requestAnimationFrame(() => {
        // Add additional throttling to prevent excessive updates
        if (now - lastUpdateTime >= UPDATE_THROTTLE) {
          const scrollLeft = sharedScrollContainer.scrollLeft;
          setScrollPosition(scrollLeft);
          updateVisibleRange(scrollLeft);
          lastUpdateTime = now;
        }
      });
    };
    
    // Initial update
    const initialScrollLeft = sharedScrollContainer.scrollLeft;
    setScrollPosition(initialScrollLeft);
    updateVisibleRange(initialScrollLeft);
    
    // Listen to scroll events on the shared container
    sharedScrollContainer.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      sharedScrollContainer.removeEventListener('scroll', handleScroll);
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [sharedScrollContainer, updateVisibleRange]);

  // Update visible range on window resize with better debouncing
  useEffect(() => {
    let timeoutId;
    let animationFrameId;
    
    const handleResize = () => {
      clearTimeout(timeoutId);
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
      
      timeoutId = setTimeout(() => {
        animationFrameId = requestAnimationFrame(() => {
          setViewportWidth(window.innerWidth);
          if (sharedScrollContainer) {
            updateVisibleRange(sharedScrollContainer.scrollLeft);
          }
        });
      }, 150); // Increased debounce for resize
    };
    
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(timeoutId);
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [updateVisibleRange, sharedScrollContainer]);

  // Optimized visible annotations filtering with better viewport calculation
  const visibleAnnotations = useMemo(() => {
    if (collapsed) return [];
    
    // Use a more efficient buffer calculation
    const RENDER_BUFFER = 200; // Fixed buffer instead of calculating per annotation
    const viewportStart = scrollPosition - RENDER_BUFFER;
    const viewportEnd = scrollPosition + viewportWidth + RENDER_BUFFER;
    
    return trackAnnotations.filter(annotation => {
      // Quick position calculation without subdivision for initial filtering
      const annotationStartX = calculatePosition(annotation.startMeasure, annotation.startBeat, 0);
      const annotationEndX = calculatePosition(annotation.endMeasure, annotation.endBeat, 0);
      
      // Simple overlap check - keep original logic which was correct
      return !(annotationEndX < viewportStart || annotationStartX > viewportEnd);
    });
  }, [trackAnnotations, calculatePosition, scrollPosition, viewportWidth, collapsed]);

  // Drawing handlers
  const handleMouseDown = (e) => {
    if (interactionDisabled || isMarkedForDeletion) return;
    // Context menu
    if (e.evt.ctrlKey) return;
    if (e.evt.button === 2) return;

    if (toolMode === 'cursor') {
      beginMarquee(e.evt.clientX, { clearOnClick: true });
      return;
    }
  
    const clickedAnnotation = e.target.findAncestor('Group');
    if (clickedAnnotation) return;

    const stage = e.target.getStage();
    const pointerPosition = stage.getPointerPosition();
    
    // Need to add scrollPosition to get the actual position in the track
    const adjustedX = pointerPosition.x + scrollPosition;
    
    setIsDrawing(true);
    setDrawStartX(adjustedX);
    setDrawCurrentX(adjustedX);
  };

  const handleMouseMove = (e) => {
    if (toolMode !== 'pencil' || interactionDisabled || isMarkedForDeletion || !isDrawing) return;

    const stage = e.target.getStage();
    const pointerPosition = stage.getPointerPosition();
    let adjustedX = pointerPosition.x + scrollPosition;

    const trackAnnotations = annotations.filter(a => a.trackId === trackId);
    const { closestLeft, closestRight } = findLeftRightAnnotationBoundaries(drawStartX, trackAnnotations, calculatePosition);

    if (adjustedX < drawStartX) {
      // Dragging to left
      adjustedX = Math.max(adjustedX, closestLeft);
    } else {
      // Dragging to right
      adjustedX = Math.min(adjustedX, closestRight);
    }

    setDrawCurrentX(adjustedX);
  };

  const handleMouseUp = () => {
    if (toolMode !== 'pencil' || interactionDisabled || isMarkedForDeletion) return;
  
    if (!isDrawing) return;

    // Get track annotations
    const trackAnnotations = annotations.filter(a => a.trackId === trackId);

    // Calculate positions with smart grid-level snapping
    let startX = Math.min(drawStartX, drawCurrentX);
    let endX = Math.max(drawStartX, drawCurrentX);
    const drawDirection = drawCurrentX >= drawStartX ? 'right' : 'left';
    const { closestLeft, closestRight } = findLeftRightAnnotationBoundaries(drawStartX, trackAnnotations, calculatePosition);

    if (drawDirection === 'right') {
      // Dragged right: endX shouldn't pass the left boundary of next annotation
      endX = Math.min(endX, closestRight);
    } else {
      // Dragged left: startX shouldn't go before right boundary of previous annotation
      startX = Math.max(startX, closestLeft);
    }

    // Convert snapped positions to musical positions with grid level awareness
    const { measure: startMeasure, beat: startBeat, subdivision: startSubdivision } =
      toMusicalPosition(startX, true);
    
    // Check if endX is at the rightmost boundary (121.1.1 position)
    // Calculate the exact 121.1.1 position using the same logic as calculatePosition
    const n_plus_one_position = calculatePosition(beatsPerMeasureMap.length + 1, 1, 1);
    
    // Fix: Ensure threshold has a minimum size to remain usable at small zoom levels
    const baseThreshold = (actualPixelsPerBeat || pixelsPerBeat * zoomLevel) * 0.3;
    const endPositionThreshold = Math.max(baseThreshold, 10); // Minimum 10 pixels for usability
    
    
    // If endX is within the threshold of the N+1 position, force it to be N+1 position
    const { measure: endMeasure, beat: endBeat, subdivision: endSubdivision } = 
      (Math.abs(endX - n_plus_one_position) <= endPositionThreshold) ? 
        { measure: beatsPerMeasureMap.length + 1, beat: 1, subdivision: 1 } :
        (() => {
          const result = toMusicalPosition(endX, true);
          // console.log("DEBUG: toMusicalPosition returned:", result, "for endX:", endX);
          return result;
        })();

    const proposedInterval = {
      start: [startMeasure, startBeat, startSubdivision || 0],
      end: [endMeasure, endBeat, endSubdivision || 0],
    };
    const minimumWidth = computeMinWidth(
      drawDirection === 'right' ? startMeasure : endMeasure,
      drawDirection === 'right' ? startBeat : endBeat,
      drawDirection === 'right' ? startSubdivision : endSubdivision,
      beatsPerMeasureMap,
      actualPixelsPerBeat,
      timeSignatures,
      currentGridLevel
    ) || actualPixelsPerBeat * MIN_ANNOTATION_WIDTH;
    const collisionResult = clampResizeInterval({
      originalInterval: proposedInterval,
      proposedInterval,
      direction: drawDirection,
      annotations,
      trackId,
      minimumWidth,
      toValue: position => calculatePosition(...position),
    });

    if (collisionResult.success) {
      const position = toPositionPatch(collisionResult.interval);
      // Find the track info to get the full instrument path if available
      const trackInfo = window.trackData?.find(t => t.id === trackId);

      const newAnnotation = {
        trackId,
        ...position,
        ...createAnnotationBlockDefaults({ trackId, trackInfo, trackAnnotations }),
      };

      const createdAnnotation = onAnnotationCreate(newAnnotation, {
        resolvePosition: ({ proposedBlock, trackBlocks }) => {
          const latestInterval = toInterval(proposedBlock);
          const latestResult = clampResizeInterval({
            originalInterval: latestInterval,
            proposedInterval: latestInterval,
            direction: drawDirection,
            annotations: trackBlocks,
            trackId,
            minimumWidth,
            toValue: position => calculatePosition(...position),
          });
          return latestResult.success ? toPositionPatch(latestResult.interval) : false;
        },
      });
      if (createdAnnotation?.id) {
        handleAnnotationSelect(createdAnnotation.id);
      }
    }

    // Reset drawing state
    setIsDrawing(false);
    setDrawStartX(null);
    setDrawCurrentX(null);
  };

  // Annotation handlers
  const handleAnnotationDrag = (id, { proposedDelta }) => {
    if (interactionDisabled || isMarkedForDeletion) return;
    const annotation = trackAnnotations.find(candidate => candidate.id === id);
    if (!annotation) return;

    const originalInterval = toInterval(annotation);
    const plan = planBlockMove({
      annotations: trackAnnotations,
      id,
      proposedDelta,
      trackId: annotation.trackId,
      sheetStart: calculatePosition(1, 1, 1),
      sheetEnd: calculatePosition(measures + 1, 1, 1),
      toValue: position => calculatePosition(...position),
      fromValue,
      movementStep: actualPixelsPerBeat / 4,
    });
    if (!plan.success) return false;

    const plannedInterval = plan.intervals[0].interval;
    const result = onAnnotationsMove([{ id, ...toPositionPatch(plannedInterval) }], {
      validate: ({ currentBlocks, proposedBlocks, trackBlocks }) => {
        if (currentBlocks.length !== 1 || proposedBlocks.length !== 1) return false;
        if (!intervalsEqual(toInterval(currentBlocks[0]), originalInterval)) return false;
        const latest = planBlockMove({
          annotations: trackBlocks,
          id,
          proposedDelta: plan.delta,
          trackId: annotation.trackId,
          sheetStart: calculatePosition(1, 1, 1),
          sheetEnd: calculatePosition(measures + 1, 1, 1),
          toValue: position => calculatePosition(...position),
          fromValue,
          movementStep: actualPixelsPerBeat / 4,
        });
        return latest.success && intervalsEqual(
          toInterval(proposedBlocks[0]),
          latest.intervals[0].interval
        );
      },
    });
    handleAnnotationSelect(id);
    return result;
  };

  const handleAnnotationResize = (id, { width, direction }) => {
    if (interactionDisabled || isMarkedForDeletion) return;
    const annotation = trackAnnotations.find(candidate => candidate.id === id);
    if (!annotation) return;
    const originalInterval = toInterval(annotation);
    const startX = calculatePosition(...originalInterval.start);
    const endX = calculatePosition(...originalInterval.end);
    const originalWidth = endX - startX;
    const proposedDelta = direction === 'right'
      ? width - originalWidth
      : originalWidth - width;
    const anchor = direction === 'right' ? originalInterval.start : originalInterval.end;
    const minimumWidth = computeMinWidth(
      anchor[0],
      anchor[1],
      anchor[2],
      beatsPerMeasureMap,
      actualPixelsPerBeat,
      timeSignatures,
      currentGridLevel
    ) || actualPixelsPerBeat * MIN_ANNOTATION_WIDTH;
    const plan = planBlockResize({
      annotations: trackAnnotations,
      id,
      direction,
      proposedDelta,
      trackId: annotation.trackId,
      sheetStart: calculatePosition(1, 1, 1),
      sheetEnd: calculatePosition(measures + 1, 1, 1),
      minimumWidth,
      toValue: position => calculatePosition(...position),
      fromValue,
      movementStep: actualPixelsPerBeat / 4,
    });
    if (!plan.success) return false;

    const position = toPositionPatch(plan.interval);
    const changes = direction === 'right'
      ? {
          endMeasure: position.endMeasure,
          endBeat: position.endBeat,
          endSubdivision: position.endSubdivision,
        }
      : {
          startMeasure: position.startMeasure,
          startBeat: position.startBeat,
          startSubdivision: position.startSubdivision,
        };

    return handleAnnotationUpdate(id, changes, {
      resolvePosition: ({ currentBlock, trackBlocks }) => {
        const currentInterval = toInterval(currentBlock);
        if (!intervalsEqual(currentInterval, originalInterval)) return false;

        const latest = planBlockResize({
          annotations: trackBlocks,
          id,
          direction,
          proposedDelta: plan.delta,
          trackId: currentBlock.trackId,
          sheetStart: calculatePosition(1, 1, 1),
          sheetEnd: calculatePosition(measures + 1, 1, 1),
          minimumWidth,
          toValue: position => calculatePosition(...position),
          fromValue,
          movementStep: actualPixelsPerBeat / 4,
        });
        if (!latest.success) return false;

        const latestPosition = toPositionPatch(latest.interval);
        return direction === 'right'
          ? {
              endMeasure: latestPosition.endMeasure,
              endBeat: latestPosition.endBeat,
              endSubdivision: latestPosition.endSubdivision,
            }
          : {
              startMeasure: latestPosition.startMeasure,
              startBeat: latestPosition.startBeat,
              startSubdivision: latestPosition.startSubdivision,
            };
      },
    });
  };

  return (
    <div 
      style={{ 
        height: actualHeight, 
        position: 'relative', 
        overflow: 'hidden',
        background: isClipboardTrack ? '#edf4ff' : (isSelected ? '#e5e5e5' : '#fff')
      }}
      ref={containerRef}
      className={className}
    >

      {isTooTall ? (
        <div 
          style={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#fff',
            border: '1px solid #000',
            color: '#f00',
            fontSize: '14px',
            fontWeight: 'bold',
            textAlign: 'center',
            padding: '10px'
          }}
        >
          Zoom level too high. Please reduce vertical zoom to see annotations.
        </div>
      ) : (
        <div 
          style={{ 
            width: totalWidth, 
            height: actualHeight,
            position: 'relative',
            // Important: Let the parent container handle scrolling natively
            left: 0,
            top: 0
          }}
          onMouseOut={() => {
            setIsDrawing(false);
            setDrawStartX(null);
            setDrawCurrentX(null);
          }}
        >
          <Stage
            width={viewportCanvasWidth} 
            height={actualHeight}
            ref={stageRef}
            onMouseDown={interactionDisabled ? undefined : handleMouseDown}
            onMouseMove={interactionDisabled ? undefined : handleMouseMove}
            onMouseUp={interactionDisabled ? undefined : handleMouseUp}
            onContextMenu={interactionDisabled || toolMode !== 'cursor' ? undefined : (e => {
              e.evt.preventDefault();
              onClipboardContextMenu({
                trackId,
                annotationId: null,
                position: { top: e.evt.clientY, left: e.evt.clientX },
              });
            })}
            style={{
              // Position absolutely within the parent container
              position: 'absolute',
              left: 0,
              top: 0
            }}
          >
            <Layer>
              {/* Background */}
              <Rect
                width={viewportCanvasWidth}
                height={actualHeight}
                fill={isClipboardTrack ? '#edf4ff' : (isSelected ? '#e5e5e5' : '#fff')}
                stroke="#000"
                strokeWidth={0.2}
              />
              
              {/* Dynamic Beat lines - render based on current grid level */}
              <BeatLines 
                measures={measures} 
                beatsPerMeasureMap={beatsPerMeasureMap} 
                calculatePosition={calculatePosition} 
                actualHeight={actualHeight}
                visibleRange={visibleRange}
                scrollPosition={scrollPosition}
                viewportWidth={viewportCanvasWidth}
                currentGridLevel={currentGridLevel}
                timeSignatures={timeSignatures}
                actualPixelsPerBeat={actualPixelsPerBeat}
              />
              
              {/* Drawing preview */}
              {!interactionDisabled && isDrawing && drawStartX !== null && drawCurrentX !== null && (
                <Rect
                  x={Math.min(drawStartX, drawCurrentX) - scrollPosition}
                  y={0}
                  width={Math.abs(drawCurrentX - drawStartX)}
                  height={actualHeight}
                  fill={PREVIEW_FILL_COLOR}
                  stroke={PREVIEW_STROKE_COLOR}
                  strokeWidth={PREVIEW_STROKE_WIDTH}
                />
              )}

              {toolMode === 'cursor' && marquee && (
                <Rect
                  x={Math.min(marquee.startX, marquee.currentX) - scrollPosition}
                  y={1}
                  width={Math.abs(marquee.currentX - marquee.startX)}
                  height={Math.max(0, actualHeight - 2)}
                  fill="rgba(52, 103, 214, 0.14)"
                  stroke="#3467d6"
                  strokeWidth={1.5}
                  dash={[7, 4]}
                  listening={false}
                />
              )}
              
              {/* Virtualized Annotations - only render visible ones */}
              {!collapsed && visibleAnnotations.map(annotation => {
                const otherAnnotations = trackAnnotations.filter(a => a.id !== annotation.id);
                
                // Calculate annotation position
                const startX = calculatePosition(
                  annotation.startMeasure, 
                  annotation.startBeat,
                  annotation.startSubdivision || 0
                );
                
                // Calculate annotation end position for proper viewport culling of long blocks
                const endX = calculatePosition(
                  annotation.endMeasure, 
                  annotation.endBeat,
                  annotation.endSubdivision || 0
                );
                
                // Adjust positions for scroll - ensure 1:1 mapping with time grid
                const displayX = startX - scrollPosition;
                const displayEndX = endX - scrollPosition;
                
                // Only render annotations that are actually visible in the canvas viewport
                // For long blocks, check both start and end positions
                const VIEWPORT_BUFFER = 200;
                const isVisible = !(displayX > viewportCanvasWidth + VIEWPORT_BUFFER || displayEndX < -VIEWPORT_BUFFER);
                
                if (isVisible) {
                  return (
                    <AnnotationBlock
                      key={annotation.id}
                      annotation={annotation}
                      pixelsPerBeat={actualPixelsPerBeat}
                      beatsPerMeasureMap={beatsPerMeasureMap}
                      calculatePosition={calculatePosition}
                      height={actualHeight}
                      isSelected={toolMode === 'cursor'
                        ? selectedIdSet.has(annotation.id)
                        : selectedAnnotationId === annotation.id}
                      isCopied={copiedIdSet.has(annotation.id)}
                      isPasted={pastedIdSet.has(annotation.id)}
                      toolMode={toolMode}
                      interactionDisabled={interactionDisabled}
                      canOpenForm={canOpenForm}
                      canSelectBlock={canSelectBlock}
                      previewOffsetX={selectedIdSet.has(annotation.id) ? groupPreviewDelta : 0}
                      collapsed={collapsed}
                      otherAnnotations={otherAnnotations}
                      onSelect={() => {
                        handleAnnotationSelect(annotation.id);
                      }}
                      onDragMove={(newPosition) => handleAnnotationDrag(annotation.id, newPosition)}
                      onCursorMarqueeStart={(e) => beginMarquee(e.evt.clientX)}
                      onCursorBlockClick={(e) => onBlockSelectionRequest({
                        trackId,
                        annotationId: annotation.id,
                        shiftKey: e.evt.shiftKey,
                        toggleKey: /Mac|iPhone|iPad/.test(navigator.platform)
                          ? e.evt.metaKey
                          : e.evt.ctrlKey,
                        position: { top: e.evt.clientY, left: e.evt.clientX },
                      })}
                      onContextMenu={(e) => onClipboardContextMenu({
                        trackId,
                        annotationId: annotation.id,
                        position: { top: e.evt.clientY, left: e.evt.clientX },
                      })}
                      onGroupDragStart={handleGroupDragStart}
                      onGroupDragMove={handleGroupDragMove}
                      onGroupDragEnd={handleGroupDragEnd}
                      onResize={({ width, direction }) =>
                        handleAnnotationResize(annotation.id, { width, direction })
                      }
                      onDoubleClick={(e) => handleSelectAnnotation(e, annotation)}
                      onDelete={() => {
                        onAnnotationDelete(annotation.id);
                      }}
                      // Adjust x position to account for scrolling
                      x={displayX}
                      scrollPosition={scrollPosition}
                      trackColor={trackColor}
                      currentGridLevel={currentGridLevel}
                      actualPixelsPerBeat={actualPixelsPerBeat}
                      totalWidth={totalWidth}
                      timeSignatures={timeSignatures}
                      isMarkedForDeletion={isMarkedForDeletion}
                    />
                  );
                }
                return null;
              })}
            </Layer>
          </Stage>
        </div>
      )}

      <Popover
        open={Boolean(anchorEl) && activeFormBlockId === draft?.blockId}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'center',
          horizontal: 'center',
        }}
        onClose={handleCloseAnnotation}
        transformOrigin={{
          vertical: 'center',
          horizontal: 'center',
        }}
        disableEnforceFocus
        disableAutoFocus
        PaperProps={{
          sx: {
            width: '900px',
            maxWidth: '90vw',
            maxHeight: '80vh',
            overflow: 'auto'
          }
        }}
      >

        {/* Add extensive debugging for annotation form attributes issue */}
        {anchorEl && (() => {
          // console.log('DEBUG - Form opened with form attributes:', {
          //   trackId,
          //   attributesProvided: Boolean(annotationFormAttributes),
          //   attributesType: annotationFormAttributes ? typeof annotationFormAttributes : 'none',
          //   attributesEmpty: annotationFormAttributes ? Object.keys(annotationFormAttributes).length === 0 : true,
          //   attributesKeys: annotationFormAttributes ? Object.keys(annotationFormAttributes) : [],
          //   annotationTrackId: selectedAnnotation?.trackId,
          //   formData: selectedAnnotation?.formData
          // });

          if (annotationFormAttributes) {
            // console.log('DEBUG - Form attribute categories:', Object.keys(annotationFormAttributes));

            // Log the first field of each category to help debug content
            Object.entries(annotationFormAttributes).forEach(([category, fields]) => {
              if (fields && fields.length > 0) {
                // console.log(`DEBUG - Sample field from ${category}:`, fields[0]);
              }
            });
          }

          return null;
        })()}

        <AnnotationForm
          key={draft?.blockId}
          ownerId={draft?.blockId}
          attributes={annotationFormAttributes}
          initialValues={draft?.values || {}}
          trackName={(() => {
            // Get the track name from the block owner's track.
            const trackInfo = window.trackData?.find(t => t.id === draftOwner?.trackId);
            return trackInfo?.name || '';
          })()}
          onChange={(values) => {
            if (draft?.blockId && draftController.change(draft.blockId, values)) {
              setDraft(draftController.getDraft());
            }
          }}
          onSubmit={(ownerId, selectedValues) => {
            // Collect all valid field names from the form definition
            const validFieldNames = [];
            if (annotationFormAttributes) {
              Object.values(annotationFormAttributes).forEach(fields => {
                fields.forEach(field => {
                  validFieldNames.push(field.name);
                });
              });
            }
            // Filter out any fields that aren't part of the original form data or valid field names
            const relevantSelectedValues = Object.keys(selectedValues).reduce((acc, key) => {
              // Include fields that:
              // 1. Were in the original form data, OR
              // 2. Are declared in the form attributes (based on field.name)
              if (draftOwner?.formData?.[key] !== undefined ||
                  validFieldNames.includes(key)) {
                acc[key] = selectedValues[key];
              }
              return acc;
            }, {});

            draftController.submit(ownerId, {
              ...relevantSelectedValues,
              completed: selectedValues.completed || false,
            });
          }}
          onSubmissionComplete={(ownerId) => {
            setDraft(null);
            setAnchorEl(null);
            closeAnnotationForm(ownerId);
          }}
        />
      </Popover>
    </div>
  );
};

export default AnnotationLayer;
